import React, { useState, useRef, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import SignatureCanvas from 'react-signature-canvas';

const WaiverModal = ({ 
  isOpen, 
  onClose, 
  onSubmit, 
  pmUser, 
  initialData = null,
  mode = 'admin', // 'admin' ou 'pm'
  waiverData = null,
  availablePMs = []
}) => {
  const [formData, setFormData] = useState({
    // CONTRATANTE (preenchido automaticamente no modo admin)
    contratante_endereco: initialData?.contratante_endereco || '',
    contratante_cidade_estado_cep: initialData?.contratante_cidade_estado_cep || '',
    contratante_telefone: initialData?.contratante_telefone || '',
    contratante_email: initialData?.contratante_email || '',
    contratante_ein: initialData?.contratante_ein || '',
    contratante_nome_cargo: initialData?.contratante_nome_cargo || '',
    
    // SUBCONTRATADO
    subcontratado_nome_completo: waiverData?.subcontratado_nome_completo || '',
    subcontratado_dba: waiverData?.subcontratado_dba || '',
    subcontratado_endereco: waiverData?.subcontratado_endereco || '',
    subcontratado_cidade_estado_cep: waiverData?.subcontratado_cidade_estado_cep || '',
    subcontratado_telefone: waiverData?.subcontratado_telefone || '',
    subcontratado_email: waiverData?.subcontratado_email || '',
    subcontratado_ssn_ein: waiverData?.subcontratado_ssn_ein || '',
    subcontratado_tipo_entidade: waiverData?.subcontratado_tipo_entidade || 'pessoa_fisica',
    subcontratado_tipo_entidade_outro: waiverData?.subcontratado_tipo_entidade_outro || '',
    subcontratado_nome_assinatura: waiverData?.subcontratado_nome_assinatura || '',
    subcontratado_ssn_ein_assinatura: waiverData?.subcontratado_ssn_ein_assinatura || ''
  });
  
  const [selectedPM, setSelectedPM] = useState(pmUser || '');
  
  const contratanteSignRef = useRef();
  const subcontratadoSignRef = useRef();
  
  // Atualizar formData quando initialData mudar
  useEffect(() => {
    if (initialData) {
      setFormData(prev => ({
        ...prev,
        contratante_endereco: initialData.contratante_endereco || '',
        contratante_cidade_estado_cep: initialData.contratante_cidade_estado_cep || '',
        contratante_telefone: initialData.contratante_telefone || '',
        contratante_email: initialData.contratante_email || '',
        contratante_ein: initialData.contratante_ein || '',
        contratante_nome_cargo: initialData.contratante_nome_cargo || ''
      }));
    }
  }, [initialData]);
  
  // Atualizar formData quando waiverData mudar (modo PM)
  useEffect(() => {
    if (waiverData && mode === 'pm') {
      setFormData(prev => ({
        ...prev,
        subcontratado_nome_completo: waiverData.subcontratado_nome_completo || '',
        subcontratado_dba: waiverData.subcontratado_dba || '',
        subcontratado_endereco: waiverData.subcontratado_endereco || '',
        subcontratado_cidade_estado_cep: waiverData.subcontratado_cidade_estado_cep || '',
        subcontratado_telefone: waiverData.subcontratado_telefone || '',
        subcontratado_email: waiverData.subcontratado_email || '',
        subcontratado_ssn_ein: waiverData.subcontratado_ssn_ein || '',
        subcontratado_tipo_entidade: waiverData.subcontratado_tipo_entidade || 'pessoa_fisica',
        subcontratado_tipo_entidade_outro: waiverData.subcontratado_tipo_entidade_outro || ''
      }));
    }
  }, [waiverData, mode]);
  
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };
  
  const handleSubmit = () => {
    if (mode === 'admin') {
      // Admin criando e enviando waiver
      // Não precisa validar dados do SUBCONTRATADO - PM preenche depois
      
      // Usar pmUser se fornecido, senão usar selectedPM
      const targetPM = pmUser || selectedPM;
      if (!targetPM) {
        alert('Selecione um PM para enviar o contrato');
        return;
      }
      
      // App.js já validou as configurações, apenas enviar
      const hoje = new Date().toISOString().split('T')[0];
      
      // Filtrar campos vazios do SUBCONTRATADO (enviar null ao invés de "")
      const cleanedFormData = {};
      Object.keys(formData).forEach(key => {
        if (key.startsWith('subcontratado_')) {
          // Apenas incluir se tiver valor
          cleanedFormData[key] = formData[key] || null;
        } else {
          cleanedFormData[key] = formData[key];
        }
      });
      
      const waiverPayload = {
        pm_user: targetPM,
        ...cleanedFormData
      };
      
      console.log('📝 Payload do modal:', waiverPayload);
      onSubmit(waiverPayload);
    } else {
      // PM assinando waiver
      // Validar campos obrigatórios
      if (!formData.subcontratado_nome_completo || !formData.subcontratado_endereco || 
          !formData.subcontratado_cidade_estado_cep || !formData.subcontratado_telefone ||
          !formData.subcontratado_email || !formData.subcontratado_ssn_ein) {
        alert('Preencha todos os campos obrigatórios');
        return;
      }
      
      const subcontratadoSignature = subcontratadoSignRef.current?.toDataURL();
      if (!subcontratadoSignature || subcontratadoSignRef.current?.isEmpty()) {
        alert('Sua assinatura é obrigatória');
        return;
      }
      
      if (!formData.subcontratado_nome_assinatura || !formData.subcontratado_ssn_ein_assinatura) {
        alert('Preencha seu nome e SSN/EIN para assinatura');
        return;
      }
      
      const signPayload = {
        subcontratado_nome_completo: formData.subcontratado_nome_completo,
        subcontratado_dba: formData.subcontratado_dba,
        subcontratado_endereco: formData.subcontratado_endereco,
        subcontratado_cidade_estado_cep: formData.subcontratado_cidade_estado_cep,
        subcontratado_telefone: formData.subcontratado_telefone,
        subcontratado_email: formData.subcontratado_email,
        subcontratado_ssn_ein: formData.subcontratado_ssn_ein,
        subcontratado_tipo_entidade: formData.subcontratado_tipo_entidade,
        subcontratado_tipo_entidade_outro: formData.subcontratado_tipo_entidade_outro,
        subcontratado_assinatura: subcontratadoSignature,
        subcontratado_nome_assinatura: formData.subcontratado_nome_assinatura,
        subcontratado_ssn_ein_assinatura: formData.subcontratado_ssn_ein_assinatura
      };
      
      onSubmit(signPayload);
    }
  };
  
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold">
            {mode === 'admin' 
              ? '📄 Criar e Enviar Waiver / Termo de Responsabilidade'
              : '✍️ Assinar Waiver / Termo de Responsabilidade'
            }
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-6">
          {mode === 'admin' ? (
            <>
              {/* Modo ADMIN: Apenas confirmar envio */}
              {pmUser ? (
                <Card className="bg-blue-50 border-blue-300">
                  <CardContent className="pt-4">
                    <p className="text-lg font-bold text-blue-800">
                      📤 Enviando Waiver para: <span className="text-blue-600">{pmUser}</span>
                    </p>
                    <p className="text-sm text-gray-600 mt-2">
                      O contrato será enviado para este PM preencher seus dados e assinar.
                    </p>
                  </CardContent>
                </Card>
              ) : (
                <Card className="bg-blue-50">
                  <CardContent className="pt-4">
                    <Label className="text-lg font-bold mb-2 block">👤 Enviar para:</Label>
                    <select
                      value={selectedPM}
                      onChange={(e) => setSelectedPM(e.target.value)}
                      className="w-full border rounded px-3 py-2"
                    >
                      <option value="">Selecione um PM...</option>
                      {availablePMs.map(pm => (
                        <option key={pm} value={pm}>{pm}</option>
                      ))}
                    </select>
                  </CardContent>
                </Card>
              )}
            
              {/* CONTRATANTE - Readonly no modo admin (já preenchido) */}
              <Card className="bg-green-50">
                <CardContent className="pt-4 space-y-3">
                  <h3 className="font-bold text-lg">✅ CONTRATANTE (PM Home Remodeling) - Dados Padrão</h3>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div><b>Endereço:</b> {formData.contratante_endereco}</div>
                    <div><b>Cidade:</b> {formData.contratante_cidade_estado_cep}</div>
                    <div><b>Telefone:</b> {formData.contratante_telefone}</div>
                    <div><b>Email:</b> {formData.contratante_email}</div>
                    <div><b>EIN:</b> {formData.contratante_ein}</div>
                    <div><b>Nome/Cargo:</b> {formData.contratante_nome_cargo}</div>
                  </div>
                </CardContent>
              </Card>
              
              {/* Nota sobre dados do PM */}
              <div className="bg-yellow-50 border border-yellow-300 rounded-lg p-3">
                <p className="text-sm text-yellow-800">
                  ℹ️ O PM irá preencher seus próprios dados (nome, endereço, SSN, etc.) quando receber e assinar o contrato.
                </p>
              </div>
            </>
          ) : (
            <>
              {/* Modo PM: Preencher dados e assinar */}
              <div className="bg-yellow-50 border-2 border-yellow-300 rounded-lg p-4">
                <p className="text-yellow-800 font-medium">
                  ⚠️ Por favor, complete seus dados abaixo, leia o contrato e assine.
                </p>
              </div>
              
              {/* Dados do SUBCONTRATADO - Editáveis para o PM */}
              <Card>
                <CardContent className="pt-4 space-y-3">
                  <h3 className="font-bold text-lg">📝 Seus Dados (SUBCONTRATADO)</h3>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label>Nome Completo *</Label>
                      <Input 
                        name="subcontratado_nome_completo" 
                        value={formData.subcontratado_nome_completo} 
                        onChange={handleChange}
                      />
                    </div>
                    <div>
                      <Label>Nome Comercial (DBA)</Label>
                      <Input 
                        name="subcontratado_dba" 
                        value={formData.subcontratado_dba} 
                        onChange={handleChange}
                        placeholder="Opcional"
                      />
                    </div>
                    <div>
                      <Label>Endereço *</Label>
                      <Input 
                        name="subcontratado_endereco" 
                        value={formData.subcontratado_endereco} 
                        onChange={handleChange}
                      />
                    </div>
                    <div>
                      <Label>Cidade/Estado/CEP *</Label>
                      <Input 
                        name="subcontratado_cidade_estado_cep" 
                        value={formData.subcontratado_cidade_estado_cep} 
                        onChange={handleChange}
                      />
                    </div>
                    <div>
                      <Label>Telefone *</Label>
                      <Input 
                        name="subcontratado_telefone" 
                        value={formData.subcontratado_telefone} 
                        onChange={handleChange}
                      />
                    </div>
                    <div>
                      <Label>Email *</Label>
                      <Input 
                        name="subcontratado_email" 
                        type="email"
                        value={formData.subcontratado_email} 
                        onChange={handleChange}
                      />
                    </div>
                    <div>
                      <Label>SSN ou EIN *</Label>
                      <Input 
                        name="subcontratado_ssn_ein" 
                        value={formData.subcontratado_ssn_ein} 
                        onChange={handleChange}
                      />
                    </div>
                    <div>
                      <Label>Tipo de Entidade *</Label>
                      <select 
                        name="subcontratado_tipo_entidade" 
                        value={formData.subcontratado_tipo_entidade} 
                        onChange={handleChange}
                        className="w-full border rounded px-3 py-2 text-sm"
                      >
                        <option value="pessoa_fisica">Pessoa Física</option>
                        <option value="llc">LLC</option>
                        <option value="corporation">Corporation</option>
                        <option value="outro">Outro</option>
                      </select>
                    </div>
                    {formData.subcontratado_tipo_entidade === 'outro' && (
                      <div className="col-span-2">
                        <Label>Especificar Outro *</Label>
                        <Input 
                          name="subcontratado_tipo_entidade_outro" 
                          value={formData.subcontratado_tipo_entidade_outro} 
                          onChange={handleChange}
                        />
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
              
              {/* Dados para Assinatura */}
              <Card>
                <CardContent className="pt-4 space-y-3">
                  <h3 className="font-bold text-lg">✍️ Dados para Assinatura</h3>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label>Nome Completo (para assinatura) *</Label>
                      <Input 
                        name="subcontratado_nome_assinatura" 
                        value={formData.subcontratado_nome_assinatura} 
                        onChange={handleChange}
                        placeholder="Seu nome completo"
                      />
                    </div>
                    <div>
                      <Label>SSN/EIN (para assinatura) *</Label>
                      <Input 
                        name="subcontratado_ssn_ein_assinatura" 
                        value={formData.subcontratado_ssn_ein_assinatura} 
                        onChange={handleChange}
                        placeholder="Seu SSN ou EIN"
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              {/* ASSINATURA PM */}
              <Card>
                <CardContent className="pt-4 space-y-4">
                  <h3 className="font-bold text-lg">✍️ Sua Assinatura Digital</h3>
                  <div>
                    <Label>Assine aqui *</Label>
                    <div className="border-2 border-blue-300 rounded bg-white">
                      <SignatureCanvas
                        ref={subcontratadoSignRef}
                        canvasProps={{
                          width: 600,
                          height: 150,
                          className: 'signature-canvas w-full'
                        }}
                      />
                    </div>
                    <Button 
                      size="sm" 
                      variant="outline" 
                      onClick={() => subcontratadoSignRef.current?.clear()}
                      className="mt-2"
                    >
                      🗑️ Limpar Assinatura
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </>
          )}
          
          {/* Botões */}
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={onClose}>
              Cancelar
            </Button>
            <Button onClick={handleSubmit} className="bg-blue-600 hover:bg-blue-700">
              {mode === 'admin' 
                ? '📤 Criar e Enviar para PM'
                : '✅ Assinar Contrato'
              }
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default WaiverModal;
